import mapboxgl from 'mapbox-gl';
import md5 from 'md5';

import { toast } from '../../toastService';
import LinkagesDataLayerService from '../../../../services/LinkagesDataLayer/LinkagesDataLayerService';
import { initializeColors } from '../../../../services/rendaIbgeService';
import getLabel from '../../../../services/LinkagesDataLayer/labels';
import formater from '../../../../services/LinkagesDataLayer/formater';
import { parseCidadeValue } from '../cidade';
import { beginLayerLoading, endLayerLoading } from '../layerLoading';

/**
 * Dados censitários / Sociodemografia — camadas Linkages/IBGE (paridade com o mapa
 * Google, DefaulMap.jsx), compartilhadas entre os mapas Mapbox.
 *
 * Contrato do componente hospedeiro:
 *  - `self.map`, `self.state`, `self.setState`;
 *  - `self.getCidadesSelecionadas()` → `[{ value: 'UF-Cidade', label }]`;
 *  - `self.getFormasDesenhadas()` → features do MapboxDraw;
 *  - chamar `initDatalayer(self)` no construtor/componentDidMount;
 *  - espalhar `DATALAYER_INITIAL_STATE` no estado inicial;
 *  - chamar `setupDatalayerLayers(self)` e `reapplyDatalayer(self)` em `initializeLayers()`.
 */

const SOURCE_ID = 'datalayer';
export const DATALAYER_LAYERS = ['datalayer-fill', 'datalayer-outline'];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
// Propriedades técnicas que não devem aparecer no popup do setor.
const IGNORE_PROPERTIES = ['dt_upload', 'gid_uf', 'gid_municipio', 'id', 'color'];
const LOADING_LABEL = 'Carregando dados censitários…';
const LOADING_TABLE_LABEL = 'Carregando tabela de dados…';

export const DATALAYER_INITIAL_STATE = {
    permissoesSociodemografia: [],
    datalayers: [],
    datalayerName: '',
    datalayerFriendlyName: null,
    datalayerColorProperty: null,
    datalayerColorPropertyOptions: [],
    datalayerYear: null,
    datalayerYears: [],
    datalayerYearOptions: {},
    datalayerFillOpacity: 0.5,
    showLegendaRenda: false,
    show_demarcacao_areas: false,
    color_ranges: {},
    colorsOptions: [],
    show_modal_datalayer_datatable: false,
    datalayers_table_data: [],
    show_modal_data_dictionary: false,
    show_modal_customizar_cores_setores: false,
    loadingDatalayer: false,
};

/** Cria os campos de instância que a feature mantém fora do estado. */
export function initDatalayer(self) {
    // Cache por hash do payload → dados já baixados de cada camada.
    self.datalayersDataCache = {};
    // Último GeoJSON colorido exibido, para reaplicar na troca de estilo do mapa.
    self.currentDatalayerGeojson = null;
}

/**
 * Source vazia + camadas de preenchimento/contorno. A cor de cada setor vem da
 * propriedade `color` (definida por initializeColors). Espelha setDatalayerStyle/
 * addDatalayerListener do mapa Google.
 */
export function setupDatalayerLayers(self) {
    if (self.map.getSource(SOURCE_ID)) return;

    self.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

    const demarcacao = self.state.show_demarcacao_areas;

    self.map.addLayer({
        id: 'datalayer-fill',
        type: 'fill',
        source: SOURCE_ID,
        paint: {
            'fill-color': ['coalesce', ['get', 'color'], '#cccccc'],
            'fill-opacity': self.state.datalayerFillOpacity,
        },
    });

    self.map.addLayer({
        id: 'datalayer-outline',
        type: 'line',
        source: SOURCE_ID,
        paint: {
            'line-color': demarcacao ? '#000000' : ['coalesce', ['get', 'color'], '#cccccc'],
            'line-width': 1,
            'line-opacity': demarcacao ? 0.5 : 0.1,
        },
    });

    const datalayerPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });

    self.map.on('click', 'datalayer-fill', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties || {};

        let body = '';
        Object.entries(p).forEach(([label, value]) => {
            if (IGNORE_PROPERTIES.includes(label)) return;
            if (value === null || value === undefined || value === '') return;

            const friendlyLabel = getLabel(label);
            const formattedValue = formater(label, value);
            body += `
                    <div class="info-window-item">
                        <span class="info-window-item-label font-weight-bold">${friendlyLabel}:</span>
                        <span class="info-window-item-value">${formattedValue}</span>
                    </div>`;
        });

        const title = LinkagesDataLayerService.getLayerLabel(self.state.datalayerName);
        const content = `
                <div class="info-window">
                    <div class="info-window-header">
                        <h5 class="info-window-title">${title}</h5>
                    </div>
                    <div class="info-window-body" style="max-height:400px;overflow-y:auto;">${body}</div>
                </div>`;

        datalayerPopup.setLngLat(e.lngLat).setHTML(content).addTo(self.map);
    });

    self.map.on('mouseenter', 'datalayer-fill', () => { self.map.getCanvas().style.cursor = 'pointer'; });
    self.map.on('mouseleave', 'datalayer-fill', () => { self.map.getCanvas().style.cursor = ''; });
}

/**
 * Carrega as permissões de sociodemografia do usuário e monta as opções de
 * "Dados censitários" filtradas por permissão (espelha Ferramentas.jsx).
 * Sem nenhuma permissão, a seção inteira não é renderizada.
 */
export async function loadSociodemografiaPermissions(self) {
    try {
        const response = await axios.get(route('user.auth'));
        const permissoes = response.data?.permissoes_sociodemografia || [];

        const requiredPermissions = LinkagesDataLayerService.getRequiredPermissionsMap();
        const datalayers = LinkagesDataLayerService.getAvailableLayers()
            .map((layerGroup) => ({
                ...layerGroup,
                options: layerGroup.options.filter(
                    (option) => permissoes.includes(requiredPermissions[option.value])
                ),
            }))
            .filter((layerGroup) => layerGroup.options.length > 0);

        self.setState({ permissoesSociodemografia: permissoes, datalayers });
    } catch (e) {
        console.error('[datalayer] erro ao carregar permissões de sociodemografia:', e);
    }
}

// Camadas que exigem a escolha de uma variável (propriedade) para colorir o mapa.
export const camadaRequerPropriedadeParaColorirMapa = (self) => [
    'renda', 'consumo_valor', 'consumo_porcentagem', 'populacao', 'indice_propriedade', 'domicilios',
].includes(self.state.datalayerName);

export const camadaRequerAno = (self) => ['trabalhador'].includes(self.state.datalayerName);
export const camadaRequerMultiplosAnos = (self) => ['expansao_urbana'].includes(self.state.datalayerName);

export async function onChangeDatalayerName(self, event) {
    const datalayerType = event.target.value;
    const mapColorPropertiesOptions = LinkagesDataLayerService.getOptionsForColorMap(datalayerType);

    await self.setState({
        datalayerName: datalayerType,
        datalayerFriendlyName: LinkagesDataLayerService.getLayerLabel(datalayerType),
        datalayerColorProperty: null,
        datalayerColorPropertyOptions: mapColorPropertiesOptions,
    });

    if (!datalayerType) {
        removeDataLayer(self);
        return;
    }

    // Estes temas não precisam de nenhuma outra informação para exibição.
    const loadDirect = ['dutovias', 'vazios_urbanos', 'area_preservacao', 'area_urbanizada', 'indicadores_municipio'];
    if (loadDirect.includes(datalayerType)) {
        await loadDataLayer(self);
    }
}

export async function onChangeDatalayerColorProperty(self, event) {
    await self.setState({ datalayerColorProperty: event.value });

    const loadDirect = ['renda', 'consumo_valor', 'consumo_porcentagem', 'populacao', 'domicilios', 'indice_propriedade'];
    if (loadDirect.includes(self.state.datalayerName)) {
        await loadDataLayer(self);
    }
}

export async function onChangeDatalayerYear(self, event) {
    await self.setState({ datalayerYear: event.value });
    await loadDataLayer(self);
}

export async function onChangeDatalayerYears(self, event) {
    await self.setState({ datalayerYears: event.value, datalayerColorProperty: 'ano' });
}

export function onChangeOpacidadePreenchimento(self, e, newValue) {
    self.setState({ datalayerFillOpacity: newValue });
    self.map?.setPaintProperty('datalayer-fill', 'fill-opacity', newValue);
}

export function setDemarcacaoAreas(self, value) {
    self.setState({ show_demarcacao_areas: value });
    if (!self.map?.getLayer('datalayer-outline')) return;
    self.map.setPaintProperty('datalayer-outline', 'line-color',
        value ? '#000000' : ['coalesce', ['get', 'color'], '#cccccc']);
    self.map.setPaintProperty('datalayer-outline', 'line-opacity', value ? 0.5 : 0.1);
}

/** Chave usada para guardar/buscar as faixas de cor customizadas por camada/variável. */
export function getColorRangeKey(self) {
    let key = self.state.datalayerName;
    if (self.state.datalayerColorProperty) {
        key = `${self.state.datalayerName}_${self.state.datalayerColorProperty}`;
    }
    return key;
}

const getCachedDataLayerData = (self, hash) => self.datalayersDataCache?.[hash]?.[self.state.datalayerName];

function putDataOnCache(self, hash, data) {
    self.datalayersDataCache = {
        ...self.datalayersDataCache,
        [hash]: { ...self.datalayersDataCache[hash], [self.state.datalayerName]: data },
    };
}

/**
 * Monta o payload das áreas desenhadas + cidades para as APIs Linkages.
 * No MapBox a forma desenhada já é um Polygon (turf), então usamos feature.geometry
 * diretamente (sem circleToPolygon). O `id` da forma casa com o nome da coluna por área.
 */
export function buildSensoPayload(self, field) {
    const drawnFeatures = (self.getFormasDesenhadas() || []).filter(
        (f) => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon'
    );

    const polygons = drawnFeatures.map((f) => ({ id: f.id, geojson: f.geometry }));

    const cidadesRaw = self.getCidadesSelecionadas() || [];
    const cidades = cidadesRaw.map((cidade) => {
        const parsed = parseCidadeValue(cidade.value) || {};
        return { uf: parsed.uf, cidade: parsed.cidade, id: cidade.value };
    });

    const retorno = { polygons, cidades };
    retorno.camada = self.state.datalayerName;
    retorno.poligonos = retorno.polygons;
    retorno.ano = self.state.datalayerYear;
    retorno.anos = self.state.datalayerYears;
    retorno.field = field !== undefined ? field : self.state.datalayerColorProperty;
    retorno.hash = md5(JSON.stringify(retorno));

    return retorno;
}

async function getDatalayerData(self) {
    const requestPayload = buildSensoPayload(self);

    const cached = getCachedDataLayerData(self, requestPayload.hash);
    if (cached) return cached;

    const response = await LinkagesDataLayerService.getDataLayer(requestPayload);
    if (response.status === 200) {
        putDataOnCache(self, requestPayload.hash, response.data);
    }
    return response.data;
}

/** Carrega a camada censitária selecionada e a exibe colorida no mapa. */
export async function loadDataLayer(self) {
    if (!self.state.datalayerName || !self.map?.getSource(SOURCE_ID)) return;

    await self.setState({ loadingDatalayer: true });
    beginLayerLoading(self, LOADING_LABEL);

    try {
        const data = await getDatalayerData(self);

        if (!data?.features?.length) {
            toast.warn('Nenhum resultado encontrado para a camada de sociodemografia selecionada.');
            removeDataLayer(self);
            return;
        }

        let rangeColors = LinkagesDataLayerService.getColorRangerForDatalayerProperty(
            self.state.datalayerName,
            self.state.datalayerColorProperty
        );

        const keyColorRange = getColorRangeKey(self);
        if (self.state.color_ranges[keyColorRange]) {
            rangeColors = self.state.color_ranges[keyColorRange];
        }

        const responseData = initializeColors(data, rangeColors, self.state.datalayerColorProperty);

        self.currentDatalayerGeojson = responseData;
        self.map.getSource(SOURCE_ID).setData(responseData);

        self.setState({
            showLegendaRenda: true,
            active_tab: 'legendas',
            left_side_bar_visible: true,
            color_ranges: { ...self.state.color_ranges, [keyColorRange]: rangeColors },
            colorsOptions: LinkagesDataLayerService.getColorsOptions(self.state.datalayerName),
        });
    } catch (error) {
        console.error('[datalayer] erro ao carregar dados censitários:', error);
        toast.error('Erro ao carregar os dados censitários.');
    } finally {
        endLayerLoading(self, LOADING_LABEL);
        await self.setState({ loadingDatalayer: false });
    }
}

/** Remove a camada censitária do mapa e zera a legenda. */
export function removeDataLayer(self) {
    self.currentDatalayerGeojson = null;
    self.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    self.setState({ showLegendaRenda: false });
}

/** Reaplica as faixas de cor editadas no modal de legenda. */
export async function handleAplyColorRanges(self) {
    if (self.state.datalayerName) {
        await loadDataLayer(self);
    }
}

export function updateRendaColorRange(self, colorRange, index) {
    const key = getColorRangeKey(self);
    const colorRanges = [...(self.state.color_ranges[key] || [])];

    if (index === -1) {
        colorRanges.push(colorRange);
    } else {
        colorRanges[index] = colorRange;
    }

    self.setState({ color_ranges: { ...self.state.color_ranges, [key]: colorRanges } });
}

export function replaceColorRanges(self, colorRanges) {
    const key = getColorRangeKey(self);
    self.setState({ color_ranges: { ...self.state.color_ranges, [key]: [...colorRanges] } });
}

export function removeRendaColorRange(self, index) {
    const key = getColorRangeKey(self);
    const colorRanges = [...(self.state.color_ranges[key] || [])];
    colorRanges.splice(index, 1);
    self.setState({ color_ranges: { ...self.state.color_ranges, [key]: colorRanges } });
}

/** Tabela de dados agregados por cidade/área (botão "Tabela de Dados"). */
export async function loadDataLayerAggregated(self) {
    if (!self.state.datalayerName) return;

    await self.setState({ loadingDatalayer: true });
    beginLayerLoading(self, LOADING_TABLE_LABEL);
    try {
        const requestPayload = buildSensoPayload(self, null);
        const response = await LinkagesDataLayerService.getAggregatedData(requestPayload);

        if (response.status === 200 && response.data.length) {
            self.setState({
                show_modal_datalayer_datatable: true,
                datalayers_table_data: response.data,
            });
        } else {
            toast.info('Nenhum dado agregado encontrado para a seleção atual.');
        }
    } catch (e) {
        console.error('[datalayer] erro ao carregar tabela de dados censitários:', e);
        toast.error('Erro ao carregar a tabela de dados.');
    } finally {
        endLayerLoading(self, LOADING_TABLE_LABEL);
        await self.setState({ loadingDatalayer: false });
    }
}

/** Busca os anos disponíveis para camadas que dependem do ano (Trabalhador, Expansão Urbana). */
export async function loadYearsOptionsForLinkagesDataLayer(self, cidades) {
    try {
        const response = await LinkagesDataLayerService.getYearsForDataLayer({
            cidades: (cidades || []).map((c) => c.label),
            estados: [],
        });

        if (response.status === 200) {
            const datalayerYearOptions = {};
            Object.keys(response.data).forEach((layerName) => {
                const years = response.data[layerName] || [];
                datalayerYearOptions[layerName] = years.map((year) => ({ value: year, label: year.toString() }));
            });
            self.setState({ datalayerYearOptions });
        }
    } catch (e) {
        console.error('[datalayer] erro ao carregar anos das camadas censitárias:', e);
    }
}

/**
 * Forma usada pelo LinkagesDataTableModal/GenericDataFormatter para nomear as colunas
 * por área. O `id` precisa casar com o `id` enviado em buildSensoPayload (feature.id).
 */
export const overlaysForDatalayerTable = (self) => self.state.drawnShapes.map((s) => ({
    id: s.drawId,
    type: s.type,
    name: s.name,
}));

/**
 * Volta a feature ao estado inicial (usado pelo LIMPAR dos mapas): apaga a camada,
 * os caches e o estado de UI. As permissões e as faixas de cor customizadas
 * sobrevivem — são do usuário, não da busca.
 *
 * @returns {object} Os campos de estado a mesclar no `setState` do hospedeiro.
 */
export function resetDatalayer(self) {
    self.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    self.currentDatalayerGeojson = null;
    self.datalayersDataCache = {};

    const { permissoesSociodemografia, datalayers, color_ranges, colorsOptions, ...resto } = DATALAYER_INITIAL_STATE;
    return { ...resto };
}

/**
 * `initializeLayers()` roda de novo a cada troca de estilo do mapa, que descarta
 * fontes e camadas — sem isto a camada censitária exibida desapareceria.
 */
export function reapplyDatalayer(self) {
    if (self.state.datalayerName && self.currentDatalayerGeojson) {
        self.map.getSource(SOURCE_ID)?.setData(self.currentDatalayerGeojson);
    }
}
